import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSport } from "../contexts/SportContext";
import FootballShopView from "../components/football/FootballShopView";
import { getEffectiveTier, getTierLabel } from "../utils/features";
import { Check, X, Crown, Zap, Star, Shield, Loader2, RefreshCw, AlertCircle, Users, Globe } from "lucide-react";
import axios from "axios";
import {
  initializeRevenueCat,
  getOfferings,
  purchasePackage,
  purchaseProduct,
  restorePurchases,
  isNativePlatform,
  getCustomerInfo,
  hasActiveSubscription,
} from "../utils/revenuecat";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TIERS = [
  {
    id: "pro",
    entitlementId: "pro",
    name: "Pro",
    weeklyPrice: "AED 9.99",
    seasonPrice: "AED 99.99",
    icon: Zap,
    color: "from-blue-500 to-blue-700",
    border: "border-blue-500/40",
    glow: "shadow-blue-500/20",
    features: [
      { text: "Ad-free experience", included: true },
      { text: "4 predictor questions per match", included: true },
      { text: "1.1x predictor points multiplier", included: true },
      { text: "3 free player stat lookups/session", included: true },
      { text: "Early cap prediction access", included: true },
      { text: "Transfer window email alerts", included: true },
      { text: "Weekly digest email", included: true },
    ],
  },
  {
    id: "pro_plus",
    entitlementId: "pro_plus",
    name: "Pro Plus",
    weeklyPrice: "AED 19.99",
    seasonPrice: "AED 199.99",
    icon: Star,
    color: "from-emerald-500 to-emerald-700",
    border: "border-emerald-500/40",
    glow: "shadow-emerald-500/20",
    popular: true,
    features: [
      { text: "Everything in Pro", included: true },
      { text: "6 predictor questions per match", included: true },
      { text: "1.2x predictor points multiplier", included: true },
      { text: "5 free player stat lookups/session", included: true },
      { text: "+2 Cr starting budget", included: true },
      { text: "25-min Match Day lock (vs 20)", included: true },
      { text: "Early transfer window access", included: true },
    ],
  },
  {
    id: "pro_max",
    entitlementId: "pro_max",
    name: "Pro Max",
    weeklyPrice: "AED 29.99",
    seasonPrice: "AED 299.99",
    icon: Crown,
    color: "from-amber-500 to-orange-600",
    border: "border-amber-500/40",
    glow: "shadow-amber-500/20",
    features: [
      { text: "Everything in Pro Plus", included: true },
      { text: "All 8 predictor questions", included: true },
      { text: "1.5x predictor points multiplier", included: true },
      { text: "Unlimited player stat lookups", included: true },
      { text: "+5 Cr starting budget", included: true },
      { text: "Mid-week captain change", included: true },
      { text: "Wildcard transfer", included: true },
      { text: "Extended match day lock", included: true },
      { text: "Early cap prediction + transfer access", included: true },
      { text: "50 daily reward points (vs 25)", included: true },
      { text: "50-point referral bonus (vs 25)", included: true },
    ],
  },
];

export default function SubscribePage() {
  const { user, dbUser, getAuthHeader, setDbUser } = useAuth();
  const { sport } = useSport();
  const currentTier = getEffectiveTier(dbUser);
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("weekly");
  
  // RevenueCat state
  const [rcInitialized, setRcInitialized] = useState(false);
  const [offerings, setOfferings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isNative, setIsNative] = useState(false);

  // Initialize RevenueCat on mount
  useEffect(() => {
    const init = async () => {
      const native = isNativePlatform();
      setIsNative(native);
      console.log("SubscribePage: Is native platform:", native);
      
      if (!native) {
        console.log("SubscribePage: Not native, skipping RevenueCat");
        setLoading(false);
        return;
      }
      
      if (!user) {
        console.log("SubscribePage: No user, waiting...");
        return;
      }
      
      setLoading(true);
      setError("");
      
      try {
        // Initialize with user's Firebase UID
        console.log("SubscribePage: Initializing RevenueCat for user:", user.uid);
        const initialized = await initializeRevenueCat(user.uid);
        console.log("SubscribePage: RevenueCat initialized:", initialized);
        setRcInitialized(initialized);
        
        if (!initialized) {
          setError("Failed to initialize payment system. Please restart the app and try again.");
          setLoading(false);
          return;
        }
        
        // Get offerings with retry
        console.log("SubscribePage: Fetching offerings...");
        let off = await getOfferings();
        
        // Retry once after a short delay if first attempt fails
        if (!off || !off.current) {
          console.log("SubscribePage: First offerings fetch failed, retrying in 2s...");
          await new Promise(r => setTimeout(r, 2000));
          off = await getOfferings();
        }
        
        console.log("SubscribePage: Offerings result:", off);
        setOfferings(off);
        
        if (!off) {
          setError("Failed to load offerings. Tap 'Retry' or restart the app.");
        } else if (!off.current) {
          setError("No subscription packages available. Make sure 'default' offering is marked as Current in RevenueCat.");
        } else {
          console.log("SubscribePage: Ready! Packages:", off.current.availablePackages?.length);
        }
      } catch (err) {
        console.error("SubscribePage: Error:", err);
        setError("Failed to load subscriptions: " + (err.message || err));
      } finally {
        setLoading(false);
      }
    };
    
    init();
  }, [user]);

  // Retry loading offerings
  const retryLoadOfferings = async () => {
    setLoading(true);
    setError("");
    try {
      // Re-init if needed
      if (!rcInitialized && user) {
        const initialized = await initializeRevenueCat(user.uid);
        setRcInitialized(initialized);
        if (!initialized) {
          setError("Failed to initialize payment system. Please restart the app.");
          setLoading(false);
          return;
        }
      }
      const off = await getOfferings();
      setOfferings(off);
      if (!off || !off.current) {
        setError("Still unable to load offerings. Please restart the app.");
      } else {
        setError("");
      }
    } catch (err) {
      setError("Retry failed: " + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  // Handle purchase
  const handlePurchase = async (tier) => {
    // Auto-retry init if not initialized
    if (!rcInitialized && user) {
      const initialized = await initializeRevenueCat(user.uid);
      setRcInitialized(initialized);
      if (!initialized) {
        setError("Payment system not ready. Please restart the app.");
        return;
      }
    }
    
    // Auto-retry offerings if missing
    let currentOfferings = offerings;
    if (!currentOfferings?.current) {
      const off = await getOfferings();
      if (off?.current) {
        setOfferings(off);
        currentOfferings = off;
        setError("");
      } else {
        setError("Subscriptions not available. Please restart the app and try again.");
        return;
      }
    }
    
    setError("");
    setSuccess("");
    setPurchasing(true);
    setSelectedTier(tier.id);
    
    try {
      const packages = currentOfferings.current.availablePackages || [];
      console.log("RevenueCat: Looking for tier:", tier.id, "period:", selectedPeriod);
      console.log("RevenueCat: Available packages:", packages.map(p => ({ 
        identifier: p.identifier, 
        productId: p.product?.identifier 
      })));
      
      // RevenueCat package identifiers follow: {period}_{tier}
      // e.g., weekly_pro, season_pro_plus, weekly_pro_max
      const targetPkgId = `${selectedPeriod}_${tier.id}`;
      
      let pkg = packages.find(p => {
        const pkgId = (p.identifier || "").toLowerCase();
        return pkgId === targetPkgId;
      });
      
      // Fallback: fuzzy match on both period and tier in package/product ID
      if (!pkg) {
        pkg = packages.find(p => {
          const pkgId = (p.identifier || "").toLowerCase();
          const productId = (p.product?.identifier || "").toLowerCase();
          const matchesPeriod = pkgId.includes(selectedPeriod) || productId.includes(selectedPeriod);
          
          if (tier.id === "pro_max") {
            return matchesPeriod && (pkgId.includes("pro_max") || productId.includes("pro_max"));
          }
          if (tier.id === "pro_plus") {
            return matchesPeriod && (pkgId.includes("pro_plus") || productId.includes("pro_plus"));
          }
          if (tier.id === "pro") {
            const hasProMax = pkgId.includes("pro_max") || productId.includes("pro_max");
            const hasProPlus = pkgId.includes("pro_plus") || productId.includes("pro_plus");
            const hasPro = pkgId.includes("pro") || productId.includes("pro");
            return matchesPeriod && hasPro && !hasProMax && !hasProPlus;
          }
          return false;
        });
      }
      
      if (!pkg) {
        console.error("RevenueCat: No package found for:", targetPkgId);
        const availableIds = packages.map(p => `${p.identifier} (${p.product?.identifier})`).join(", ");
        setError(`Package "${targetPkgId}" not found. Available: ${availableIds || "none"}`);
        setPurchasing(false);
        return;
      }
      
      console.log("RevenueCat: Purchasing package:", pkg.identifier, "product:", pkg.product?.identifier);
      
      // Make purchase
      const result = await purchasePackage(pkg);
      
      if (result.cancelled) {
        console.log("RevenueCat: Purchase cancelled by user");
        setPurchasing(false);
        return;
      }
      
      if (result.success) {
        console.log("RevenueCat: Purchase successful!");
        // Sync with backend
        const customerInfo = await getCustomerInfo();
        if (customerInfo) {
          await axios.post(
            `${API}/subscription/restore`,
            { entitlements: customerInfo.entitlements },
            { headers: getAuthHeader() }
          );
          
          // Immediately re-fetch user profile from backend to activate Pro features
          try {
            const meRes = await axios.get(`${API}/auth/me`, { headers: getAuthHeader() });
            if (meRes.data) {
              setDbUser(meRes.data);
            }
          } catch {}
          
          // Fallback: poll for tier update if not yet reflected
          const expectedTier = tier.id;
          let tierUpdated = dbUser?.tier === expectedTier;
          if (!tierUpdated) {
            for (let i = 0; i < 5; i++) {
              await new Promise(r => setTimeout(r, 2000));
              try {
                const pollRes = await axios.get(`${API}/auth/me`, { headers: getAuthHeader() });
                if (pollRes.data?.tier === expectedTier) {
                  setDbUser(pollRes.data);
                  tierUpdated = true;
                  break;
                }
              } catch {}
            }
          }
          
          if (!tierUpdated) {
            setSuccess(`Successfully subscribed to ${tier.name}! Your subscription is being activated — pull down to refresh.`);
          } else {
            setSuccess(`Successfully subscribed to ${tier.name}!`);
          }
        } else {
          setSuccess(`Successfully subscribed to ${tier.name}!`);
        }
      }
    } catch (err) {
      console.error("Purchase error:", err);
      setError(err.message || "Purchase failed. Please try again.");
    } finally {
      setPurchasing(false);
    }
  };

  // Handle restore purchases
  const handleRestore = async () => {
    if (!rcInitialized) {
      setError("RevenueCat not initialized");
      return;
    }
    
    setError("");
    setSuccess("");
    setRestoring(true);
    
    try {
      const customerInfo = await restorePurchases();
      
      if (hasActiveSubscription(customerInfo)) {
        // Sync with backend
        const response = await axios.post(
          `${API}/subscription/restore`,
          { entitlements: customerInfo.entitlements },
          { headers: getAuthHeader() }
        );
        
        if (response.data.restored) {
          // Re-fetch full user profile to ensure all Pro features activate
          try {
            const meRes = await axios.get(`${API}/auth/me`, { headers: getAuthHeader() });
            if (meRes.data) {
              setDbUser(meRes.data);
            }
          } catch {
            // Fallback: use response data
            if (dbUser) {
              setDbUser({
                ...dbUser,
                tier: response.data.tier,
                tier_expires_at: response.data.expires_at
              });
            }
          }
          setSuccess(`Subscription restored: ${getTierLabel(response.data.tier)}`);
        } else {
          setError("No active subscription found to restore.");
        }
      } else {
        setError("No active subscription found to restore.");
      }
    } catch (err) {
      console.error("Restore error:", err);
      setError(err.message || "Failed to restore purchases.");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="subscribe-page">
      {sport === "football" ? <FootballShopView user={user} /> : (
      <>
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-1.5 mb-4">
          <Crown className="w-4 h-4 text-amber-400" />
          <span className="text-amber-300 text-sm font-medium">APL Pro</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
          APL Pro Plans
        </h1>
        <p className="text-slate-400 text-base max-w-md mx-auto">
          More predictions. No ads. Better rewards.
        </p>
        <p className="text-slate-500 text-sm mt-2">
          Not on the app?{" "}
          <a
            href="https://www.instagram.com/apl.fantasycricket?igsh=ZWd4ZTgzcTBoeXNl"
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-400 underline underline-offset-2 hover:text-emerald-300"
            data-testid="instagram-dm-link"
          >
            DM us
          </a>{" "}
          to purchase Pro plans.
        </p>
        {currentTier !== "free" && (
          <div className="mt-3 inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-1.5">
            <Shield className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-300 text-sm">
              Current plan: <strong>{getTierLabel(currentTier)}</strong>
            </span>
          </div>
        )}
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3 text-red-300">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <span className="text-sm">{error}</span>
            {isNative && (
              <button 
                onClick={retryLoadOfferings}
                disabled={loading}
                className="mt-2 px-3 py-1 text-xs bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-lg transition-colors flex items-center gap-1"
                data-testid="retry-offerings-btn"
              >
                <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                Retry
              </button>
            )}
          </div>
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-center gap-3 text-emerald-300">
          <Check className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{success}</span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
        </div>
      )}

      {/* Tier Cards - Horizontal scroll on mobile */}
      {!loading && (
        <>
          {/* Period Toggle */}
          <div className="flex items-center justify-center mb-4 mt-4" data-testid="period-toggle">
            <div className="bg-slate-800/80 border border-slate-700/50 rounded-xl p-1 flex">
              <button
                onClick={() => setSelectedPeriod("weekly")}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                  selectedPeriod === "weekly"
                    ? "bg-emerald-500 text-white shadow-lg"
                    : "text-slate-400 hover:text-white"
                }`}
                data-testid="period-weekly-btn"
              >
                Weekly
              </button>
              <button
                onClick={() => setSelectedPeriod("season")}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                  selectedPeriod === "season"
                    ? "bg-emerald-500 text-white shadow-lg"
                    : "text-slate-400 hover:text-white"
                }`}
                data-testid="period-season-btn"
              >
                3-Month Pass
                <span className="ml-1.5 text-[10px] text-emerald-300 font-bold">SAVE</span>
              </button>
            </div>
          </div>

          <div className="pb-4 -mx-4 px-4 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none">
            <div className="flex gap-4 pl-0 md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-5">
              {TIERS.map((tier) => {
                const Icon = tier.icon;
                const isCurrentTier = currentTier === tier.id;
                const canPurchase = isNative && rcInitialized && !isCurrentTier;
                const displayPrice = selectedPeriod === "weekly" ? tier.weeklyPrice : tier.seasonPrice;
                const periodLabel = selectedPeriod === "weekly" ? "/week" : "/3 months";
                
                return (
                  <div
                    key={tier.id}
                    data-testid={`tier-card-${tier.id}`}
                    className={`relative rounded-2xl border ${tier.border} bg-slate-800/80 backdrop-blur-sm p-4 transition-all duration-300 hover:scale-[1.02] w-[280px] min-w-[280px] md:w-auto md:min-w-0 flex-shrink-0 snap-center overflow-visible ${
                      tier.popular ? `shadow-lg ${tier.glow} mt-3 md:mt-3` : ""
                    } ${selectedTier === tier.id ? "ring-2 ring-white/30" : ""}`}
                    onClick={() => setSelectedTier(tier.id)}
                  >
                  {tier.popular && (
                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
                      MOST POPULAR
                    </div>
                  )}

                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${tier.color} flex items-center justify-center mb-2 ${tier.popular ? 'mt-2' : ''}`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>

                  <h3 className="text-base font-bold text-white mb-2">{tier.name}</h3>
                  
                  {/* Single price based on selected period */}
                  <div className="mb-3">
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl font-bold text-white">{displayPrice}</span>
                      <span className="text-slate-400 text-xs">{periodLabel}</span>
                    </div>
                  </div>

                  <ul className="space-y-1.5 mb-4">
                    {tier.features.map((feat, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-xs">
                      {feat.included ? (
                        <Check className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />
                      ) : (
                        <X className="w-3 h-3 text-slate-600 mt-0.5 flex-shrink-0" />
                      )}
                      <span className={feat.included ? "text-slate-300" : "text-slate-500"}>
                        {feat.text}
                      </span>
                    </li>
                  ))}
                </ul>

                {isCurrentTier ? (
                  <div
                    data-testid={`tier-current-${tier.id}`}
                    className="w-full py-2 rounded-xl bg-slate-700 text-slate-400 text-center text-xs font-medium"
                  >
                    Current Plan
                  </div>
                ) : canPurchase ? (
                  <button
                    data-testid={`tier-btn-${tier.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePurchase(tier);
                    }}
                    disabled={purchasing}
                    className={`w-full py-2 rounded-xl bg-gradient-to-r ${tier.color} text-white text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-60`}
                  >
                    {purchasing && selectedTier === tier.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : null}
                    Subscribe
                  </button>
                ) : (
                  <button
                    data-testid={`tier-btn-${tier.id}`}
                    className={`w-full py-2 rounded-xl bg-gradient-to-r ${tier.color} text-white text-xs font-semibold opacity-60 cursor-not-allowed`}
                    disabled
                  >
                    {isNative ? "Coming Soon" : "App Only"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        </div>
        </>
      )}

      {/* Restore Purchases Button */}
      {isNative && rcInitialized && (
        <div className="text-center mb-6">
          <button
            onClick={handleRestore}
            disabled={restoring}
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors"
            data-testid="restore-purchases-btn"
          >
            {restoring ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Restore Purchases
          </button>
        </div>
      )}

      {/* Free Tier Comparison */}
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl p-5 mb-6" data-testid="free-tier-info">
        <h3 className="text-white font-semibold mb-3">Free Tier Includes:</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-300">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-slate-500" />
            <span>2 predictor questions per match</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-slate-500" />
            <span>1 free player stat lookup/session</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-slate-500" />
            <span>Full season + daily mode access</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-slate-500" />
            <span>Mini-leagues with friends</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-slate-500" />
            <span>25 daily reward points</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-slate-500" />
            <span>Upgrade to Pro to unlock more questions</span>
          </div>
        </div>
      </div>

      {/* League Consumables Section */}
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-slate-700/50 rounded-2xl p-5 mb-6" data-testid="league-consumables">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-emerald-400" />
          <h3 className="text-white font-semibold">League Upgrades</h3>
        </div>
        <p className="text-slate-400 text-sm mb-4">Create additional leagues and expand member limits!</p>
        
        {/* League Slots - Row 1 */}
        <p className="text-slate-500 text-xs uppercase tracking-wide mb-2">League Slots</p>
        <div className="grid grid-cols-2 gap-3 mb-4">
          {/* Private League Slot */}
          <div className="bg-slate-900/60 border border-slate-600/50 rounded-xl p-4 text-center hover:border-purple-500/50 transition-all">
            <div className="w-12 h-12 mx-auto mb-2 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-purple-400" />
            </div>
            <h4 className="text-white font-semibold text-sm mb-1">Private League</h4>
            <p className="text-slate-400 text-xs mb-2">Invite-only</p>
            <div className="text-lg font-bold text-white">AED 4.99</div>
            {isNative ? (
              <button
                onClick={async () => {
                  try {
                    console.log("RevenueCat: Purchasing product: apl_league_private_v2");
                    const result = await purchaseProduct("apl_league_private_v2");
                    console.log("RevenueCat: Purchase result:", result);
                    if (result.success) {
                      setSuccess("Private league slot purchased!");
                    } else if (result.cancelled) {
                      // User cancelled - no error
                    } else {
                      setError("Purchase failed. Please try again.");
                    }
                  } catch (e) {
                    console.error("RevenueCat: Purchase error:", e);
                    setError("Purchase failed: " + (e.message || "Product not found. Check RevenueCat configuration."));
                  }
                }}
                disabled={purchasing}
                className="w-full mt-2 px-3 py-2 bg-purple-500/20 text-purple-400 rounded-lg text-xs font-medium hover:bg-purple-500/30 transition-all disabled:opacity-50"
              >
                {purchasing ? "..." : "Buy"}
              </button>
            ) : (
              <p className="text-slate-500 text-xs mt-2">App only</p>
            )}
          </div>
          
          {/* Public League Slot */}
          <div className="bg-slate-900/60 border border-slate-600/50 rounded-xl p-4 text-center hover:border-emerald-500/50 transition-all">
            <div className="w-12 h-12 mx-auto mb-2 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <Globe className="w-6 h-6 text-emerald-400" />
            </div>
            <h4 className="text-white font-semibold text-sm mb-1">Public League</h4>
            <p className="text-slate-400 text-xs mb-2">Open to all</p>
            <div className="text-lg font-bold text-white">AED 9.99</div>
            {isNative ? (
              <button
                onClick={async () => {
                  try {
                    await purchaseProduct("apl_league_public_v2");
                    setSuccess("Public league slot purchased!");
                  } catch (e) {
                    setError("Purchase failed");
                  }
                }}
                disabled={purchasing}
                className="w-full mt-2 px-3 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg text-xs font-medium hover:bg-emerald-500/30 transition-all disabled:opacity-50"
              >
                {purchasing ? "..." : "Buy"}
              </button>
            ) : (
              <p className="text-slate-500 text-xs mt-2">App only</p>
            )}
          </div>
        </div>

        {/* Member Upgrades - Row 2 */}
        <p className="text-slate-500 text-xs uppercase tracking-wide mb-2">Member Capacity</p>
        <div className="grid grid-cols-3 gap-2">
          {/* 15 Members */}
          <div className="bg-slate-900/60 border border-slate-600/50 rounded-xl p-3 text-center hover:border-emerald-500/50 transition-all">
            <div className="w-10 h-10 mx-auto mb-2 bg-emerald-500/20 rounded-lg flex items-center justify-center">
              <span className="text-emerald-400 font-bold text-sm">15</span>
            </div>
            <h4 className="text-white font-semibold text-xs mb-1">15 Members</h4>
            <div className="text-base font-bold text-white">AED 4.99</div>
            {isNative ? (
              <button
                onClick={async () => {
                  try {
                    await purchaseProduct("apl_league_size_15_v2");
                    setSuccess("Capacity upgraded to 15!");
                  } catch (e) {
                    setError("Purchase failed");
                  }
                }}
                disabled={purchasing}
                className="w-full mt-2 px-2 py-1.5 bg-emerald-500/20 text-emerald-400 rounded-lg text-xs font-medium hover:bg-emerald-500/30 transition-all disabled:opacity-50"
              >
                Buy
              </button>
            ) : (
              <p className="text-slate-500 text-xs mt-2">App only</p>
            )}
          </div>

          {/* 30 Members */}
          <div className="bg-slate-900/60 border border-slate-600/50 rounded-xl p-3 text-center hover:border-blue-500/50 transition-all">
            <div className="w-10 h-10 mx-auto mb-2 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <span className="text-blue-400 font-bold text-sm">30</span>
            </div>
            <h4 className="text-white font-semibold text-xs mb-1">30 Members</h4>
            <div className="text-base font-bold text-white">AED 9.99</div>
            {isNative ? (
              <button
                onClick={async () => {
                  try {
                    await purchaseProduct("apl_league_size_30_v2");
                    setSuccess("Capacity upgraded to 30!");
                  } catch (e) {
                    setError("Purchase failed");
                  }
                }}
                disabled={purchasing}
                className="w-full mt-2 px-2 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg text-xs font-medium hover:bg-blue-500/30 transition-all disabled:opacity-50"
              >
                Buy
              </button>
            ) : (
              <p className="text-slate-500 text-xs mt-2">App only</p>
            )}
          </div>

          {/* 50 Members */}
          <div className="bg-slate-900/60 border border-slate-600/50 rounded-xl p-3 text-center hover:border-purple-500/50 transition-all">
            <div className="w-10 h-10 mx-auto mb-2 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <span className="text-purple-400 font-bold text-sm">50</span>
            </div>
            <h4 className="text-white font-semibold text-xs mb-1">50 Members</h4>
            <div className="text-base font-bold text-white">AED 14.99</div>
            {isNative ? (
              <button
                onClick={async () => {
                  try {
                    await purchaseProduct("apl_league_size_50_v2");
                    setSuccess("Capacity upgraded to 50!");
                  } catch (e) {
                    setError("Purchase failed");
                  }
                }}
                disabled={purchasing}
                className="w-full mt-2 px-2 py-1.5 bg-purple-500/20 text-purple-400 rounded-lg text-xs font-medium hover:bg-purple-500/30 transition-all disabled:opacity-50"
              >
                Buy
              </button>
            ) : (
              <p className="text-slate-500 text-xs mt-2">App only</p>
            )}
          </div>
        </div>
      </div>

      {/* Platform Notice */}
      {!isNative && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="w-full">
              <p className="text-amber-200 text-sm font-medium">Subscriptions available in the mobile app</p>
              <p className="text-amber-300/70 text-xs mt-1">
                Download the APL app from the App Store to subscribe to Pro plans.
              </p>
              <div className="mt-3 space-y-2">
                <a
                  href="https://apps.apple.com/app/id6760789788"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-slate-900 border border-slate-600 text-white px-3 py-2.5 rounded-lg text-xs font-semibold w-full hover:bg-slate-700 transition-colors"
                  data-testid="subscribe-download-ios-btn"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                  Download on the App Store
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.apnapremierleague.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white px-3 py-2.5 rounded-lg text-xs font-semibold w-full hover:opacity-90 transition-opacity"
                  data-testid="subscribe-download-android-btn"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  Google Play
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <p className="text-center text-slate-500 text-xs">
        {isNative && rcInitialized 
          ? "Subscriptions are processed securely through the App Store."
          : "In-app purchases require the native mobile app. Tier assignment currently available via admin for web users."}
      </p>

      <div className="text-center text-xs text-slate-400 px-4 pb-8 space-y-3 mt-4 border-t border-slate-700/50 pt-4">
        <p className="leading-relaxed">
          Subscriptions auto-renew unless cancelled at least 24 hours before 
          the end of the current period. Manage or cancel anytime in your 
          Apple ID Settings.
        </p>
        <div className="flex justify-center gap-6">
          <a 
            href="https://apnapremierleague.com/privacy" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-emerald-400 underline"
          >
            Privacy Policy
          </a>
          <a 
            href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-emerald-400 underline"
          >
            Terms of Use
          </a>
        </div>
      </div>
      </>
      )}
    </div>
  );
}
